/**
 * Extracts a human-readable message from an unknown thrown value.
 * Use this in catch blocks instead of repeating the `instanceof Error` ternary.
 */
export function getErrorMessage(err: unknown, fallback = 'Unknown error'): string {
  return err instanceof Error ? err.message : fallback;
}

/** Internal error identifiers like 'INVALID_REQUEST' or 'WS_CLOSED: detail'. */
const INTERNAL_CODE_RE = /^[A-Z][A-Z0-9_]{2,}(:[\s\S]*)?$/;
/** apiFetch's fallback when the server returns no message body. */
const HTTP_STATUS_RE = /^Request failed: \d{3}$/;

/**
 * Extracts a message from a thrown value that is safe to show to the user.
 *
 * Server responses that carry a human-readable message (e.g. permission
 * explanations) pass through unchanged. Internal error codes, bare HTTP
 * status text, and network-level TypeErrors are replaced with `fallback`,
 * so raw codes never reach the UI. Always renders errors through this (or
 * an equivalent code→message map), never via err.message directly.
 */
export function friendlyErrorMessage(err: unknown, fallback: string): string {
  if (!(err instanceof Error) || !err.message) return fallback;
  // Browser network failures ("Failed to fetch") surface as TypeError.
  if (err.name === 'TypeError') {
    return 'Network error - check your connection and try again.';
  }
  const msg = err.message.trim();
  // Firebase SDK errors read like "Firebase: Error (auth/too-many-requests)."
  if (msg.startsWith('Firebase:')) return fallback;
  if (INTERNAL_CODE_RE.test(msg) || HTTP_STATUS_RE.test(msg)) return fallback;
  return msg;
}

/**
 * Returns true when a message stored or transmitted as user-facing text is
 * actually an internal code (e.g. 'BUDGET_EXCEEDED' persisted by an older
 * server version). Use to gate rendering of already-stringified errors.
 */
export function looksLikeInternalError(message: string): boolean {
  const msg = message.trim();
  return INTERNAL_CODE_RE.test(msg) || HTTP_STATUS_RE.test(msg);
}
